/* eslint-disable @atlaskit/platform/one-value-export-per-file -- this controller exposes one cohesive persistence boundary plus its pure codec and eligibility helpers */
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { EMPTY_CONVERSATION_ID } from '@atlassian/conversation-assistant-store/controllers/chat';
import {
	LS_CHAT_LOAD_PREFERENCE_KEY,
	LS_RECENT_CONVERSATION_ACTIVITY_KEY,
} from '@atlassian/conversation-assistant-utils/nonEssentialLocalStorage/nonEssentialLocalStorageKeys';
import { withNonEssentialLocalStorage } from '@atlassian/conversation-assistant-utils/nonEssentialLocalStorage/withNonEssentialLocalStorage';

import { getChatLoadPreferenceKey } from '../../common/constants/getChatLoadPreferenceKey';
import { getRecentConversationActivityKey } from '../../common/constants/getRecentConversationActivityKey';
import { setLastMessageSentAt } from '../last-message-sent/setLastMessageSentAt';

export const PERSISTED_CONVERSATION_RESTORATION_GATE =
	'rovo_chat_persisted_conversation_restoration';

export const RESTORATION_FUTURE_TIMESTAMP_TOLERANCE_MS = 60_000;

export type PersistedConversationActivity = {
	version: 1;
	conversationId: string;
	lastMessageSentAt: number | null;
};

export type RecentConversationPersistenceEvent =
	| {
			type: 'conversation-selected';
			siteId: string;
			conversationId: string;
			clearLegacyLastMessageSentAt?: boolean;
	  }
	| {
			type: 'message-sent';
			siteId: string;
			conversationId: string;
			lastMessageSentAt: number;
	  };

const getBrowserLocalStorage = (): Storage | null => {
	// eslint-disable-next-line @atlaskit/platform/no-direct-web-storage-usage -- all reads and writes are protected by the non-essential storage kill switch at their call sites
	return typeof localStorage === 'undefined' ? null : localStorage;
};

const isPersistedConversationActivity = (
	value: unknown,
): value is PersistedConversationActivity => {
	if (typeof value !== 'object' || value === null || Array.isArray(value)) {
		return false;
	}

	const candidate = value as Partial<PersistedConversationActivity>;
	return (
		candidate.version === 1 &&
		typeof candidate.conversationId === 'string' &&
		candidate.conversationId.trim().length > 0 &&
		(candidate.lastMessageSentAt === null ||
			(typeof candidate.lastMessageSentAt === 'number' &&
				Number.isFinite(candidate.lastMessageSentAt)))
	);
};

export const parsePersistedConversationActivity = (
	rawValue: string | null,
): PersistedConversationActivity | null => {
	if (!rawValue) {
		return null;
	}

	try {
		const parsed: unknown = JSON.parse(rawValue);
		return isPersistedConversationActivity(parsed) ? parsed : null;
	} catch {
		return null;
	}
};

export const serializePersistedConversationActivity = (
	activity: PersistedConversationActivity,
): string | null => (isPersistedConversationActivity(activity) ? JSON.stringify(activity) : null);

export const isPersistedConversationRestorationEnabled = (): boolean =>
	fg(PERSISTED_CONVERSATION_RESTORATION_GATE);

export const getPersistedRecentConversationId = (siteId: string): string | null =>
	withNonEssentialLocalStorage(LS_CHAT_LOAD_PREFERENCE_KEY, () => {
		// eslint-disable-next-line @atlaskit/platform/no-direct-web-storage-usage -- protected by the non-essential storage kill switch
		return getBrowserLocalStorage()?.getItem(getChatLoadPreferenceKey(siteId)) ?? null;
	});

const getPersistedConversationActivityWithoutGateCheck = (
	siteId: string,
): PersistedConversationActivity | null => {
	const rawValue = withNonEssentialLocalStorage(LS_RECENT_CONVERSATION_ACTIVITY_KEY, () => {
		// eslint-disable-next-line @atlaskit/platform/no-direct-web-storage-usage -- protected by the non-essential storage kill switch
		return getBrowserLocalStorage()?.getItem(getRecentConversationActivityKey(siteId)) ?? null;
	});

	return parsePersistedConversationActivity(rawValue);
};

export const getPersistedConversationActivity = (
	siteId: string,
): PersistedConversationActivity | null =>
	isPersistedConversationRestorationEnabled()
		? getPersistedConversationActivityWithoutGateCheck(siteId)
		: null;

const writeLegacyConversationId = (siteId: string, conversationId: string): void => {
	withNonEssentialLocalStorage(LS_CHAT_LOAD_PREFERENCE_KEY, () => {
		// eslint-disable-next-line @atlaskit/platform/no-direct-web-storage-usage -- protected by the non-essential storage kill switch
		getBrowserLocalStorage()?.setItem(getChatLoadPreferenceKey(siteId), conversationId);
	});
};

const writePersistedConversationActivity = (
	siteId: string,
	activity: PersistedConversationActivity,
): void => {
	const serialized = serializePersistedConversationActivity(activity);
	if (serialized === null) {
		return;
	}

	withNonEssentialLocalStorage(LS_RECENT_CONVERSATION_ACTIVITY_KEY, () => {
		// eslint-disable-next-line @atlaskit/platform/no-direct-web-storage-usage -- protected by the non-essential storage kill switch
		getBrowserLocalStorage()?.setItem(getRecentConversationActivityKey(siteId), serialized);
	});
};

/**
 * Single semantic mutation boundary for the persisted recent conversation and its sent activity.
 * The versioned sidecar is written last so it acts as the coherent association commit record.
 */
export const persistRecentConversation = (event: RecentConversationPersistenceEvent): void => {
	const isRestorationEnabled = isPersistedConversationRestorationEnabled();

	if (event.type === 'conversation-selected') {
		const existingActivity = isRestorationEnabled
			? getPersistedConversationActivityWithoutGateCheck(event.siteId)
			: null;

		writeLegacyConversationId(event.siteId, event.conversationId);
		if (event.clearLegacyLastMessageSentAt) {
			setLastMessageSentAt(null);
		}

		if (isRestorationEnabled) {
			writePersistedConversationActivity(event.siteId, {
				version: 1,
				conversationId: event.conversationId,
				lastMessageSentAt:
					event.conversationId !== EMPTY_CONVERSATION_ID &&
					existingActivity?.conversationId === event.conversationId
						? existingActivity.lastMessageSentAt
						: null,
			});
		}
		return;
	}

	if (!isRestorationEnabled) {
		setLastMessageSentAt(event.lastMessageSentAt);
		return;
	}

	writeLegacyConversationId(event.siteId, event.conversationId);
	setLastMessageSentAt(event.lastMessageSentAt);
	writePersistedConversationActivity(event.siteId, {
		version: 1,
		conversationId: event.conversationId,
		lastMessageSentAt: event.lastMessageSentAt,
	});
};

export const isPersistedConversationActivityRestorable = ({
	selectedConversationId,
	activity,
	now,
	timeout,
}: {
	selectedConversationId: string;
	activity: PersistedConversationActivity | null;
	now: number;
	timeout: number;
}): boolean => {
	if (
		selectedConversationId === EMPTY_CONVERSATION_ID ||
		!activity ||
		activity.conversationId !== selectedConversationId ||
		activity.lastMessageSentAt === null ||
		!Number.isFinite(now) ||
		!Number.isFinite(timeout) ||
		timeout <= 0
	) {
		return false;
	}

	const futureOffset = activity.lastMessageSentAt - now;
	if (futureOffset > RESTORATION_FUTURE_TIMESTAMP_TOLERANCE_MS) {
		return false;
	}

	const activityAge = Math.max(0, now - activity.lastMessageSentAt);
	return activityAge < timeout;
};
